import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import { useMemo } from 'react';

interface MarkdownContentProps {
  content: string;
  className?: string;
}

/**
 * Checks if content looks like HTML (from TipTap editor)
 */
/**
 * Block-level tags TipTap emits as the first node of HTML content. Matched by
 * tag name only, so an opening tag carrying attributes (`<p class="x">`,
 * `<img src="..." alt="...">`) is recognised too — matching on the literal
 * `'<p>'` missed those and silently sent them down the markdown path, where
 * raw HTML is escaped and shown to the reader as literal source.
 */
const HTML_CONTENT_TAGS = new Set([
  'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'blockquote', 'pre', 'div',
  'img', 'table', 'figure', 'hr',
]);

function isHtmlContent(text: string): boolean {
  if (!text) return false;
  const match = /^\s*<([a-zA-Z][a-zA-Z0-9]*)/.exec(text);
  return match !== null && HTML_CONTENT_TAGS.has(match[1].toLowerCase());
}

/**
 * Preprocesses content to handle escaped newlines and other formatting issues.
 */
function preprocessContent(text: string): string {
  if (!text) return '';

  // Convert literal \n strings to actual newlines
  let processed = text.replace(/\\n/g, '\n');

  // Convert literal \t strings to actual tabs
  processed = processed.replace(/\\t/g, '\t');

  return processed;
}

export function MarkdownContent({ content, className = '' }: MarkdownContentProps) {
  const processedContent = useMemo(() => preprocessContent(content), [content]);
  const isHtml = useMemo(() => isHtmlContent(content), [content]);

  // For HTML content (from TipTap editor), render directly with sanitization
  if (isHtml) {
    const sanitizedHtml = DOMPurify.sanitize(processedContent, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 's', 'strike', 'del',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
        'a', 'img', 'hr', 'span', 'div', 'figure', 'figcaption',
        // Tables are reachable content now that isHtmlContent recognises <table>;
        // without these the sanitizer stripped a table down to its bare text.
        'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
        'colgroup', 'col'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'target', 'rel'],
    });

    return (
      <div
        className={`prose prose-sm max-w-none dark:prose-invert ${className}`}
        style={{
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          lineHeight: 1.7,
          letterSpacing: '0.01em',
        }}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    );
  }

  // For markdown content (legacy), use ReactMarkdown
  return (
    <div
      className={`prose prose-sm max-w-none dark:prose-invert ${className}`}
      style={{
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        lineHeight: 1.7,
        letterSpacing: '0.01em',
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Open links in new tab
          a: ({ href, children, ...props }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary hover:underline"
              style={{ overflowWrap: 'break-word', wordBreak: 'break-all' }}
              {...props}
            >
              {children}
            </a>
          ),
          // Style code blocks
          code: ({ className, children, ...props }) => {
            const isInline = !className;
            return isInline ? (
              <code
                className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono"
                style={{ overflowWrap: 'break-word', wordBreak: 'break-all' }}
                {...props}
              >
                {children}
              </code>
            ) : (
              <code className={`${className} font-mono`} style={{ lineHeight: 1.5 }} {...props}>
                {children}
              </code>
            );
          },
          // Style pre blocks
          pre: ({ children, ...props }) => (
            <pre
              className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono"
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                lineHeight: 1.5,
                marginTop: '1.25em',
                marginBottom: '1.25em',
              }}
              {...props}
            >
              {children}
            </pre>
          ),
          // Paragraphs with better spacing
          p: ({ children, ...props }) => (
            <p
              style={{
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                marginTop: '1em',
                marginBottom: '1em',
              }}
              {...props}
            >
              {children}
            </p>
          ),
          // Headings with proper spacing
          h1: ({ children, ...props }) => (
            <h1 style={{ marginTop: '1.5em', marginBottom: '0.75em', lineHeight: 1.3 }} {...props}>
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 style={{ marginTop: '1.5em', marginBottom: '0.75em', lineHeight: 1.3 }} {...props}>
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 style={{ marginTop: '1.25em', marginBottom: '0.5em', lineHeight: 1.4 }} {...props}>
              {children}
            </h3>
          ),
          // Lists with proper spacing
          ul: ({ children, ...props }) => (
            <ul style={{ marginTop: '1em', marginBottom: '1em', paddingLeft: '1.5em' }} {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol style={{ marginTop: '1em', marginBottom: '1em', paddingLeft: '1.5em' }} {...props}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li style={{ marginTop: '0.5em', marginBottom: '0.5em' }} {...props}>
              {children}
            </li>
          ),
          // Blockquotes
          blockquote: ({ children, ...props }) => (
            <blockquote
              className="border-l-4 border-muted-foreground/30 pl-4 italic text-muted-foreground"
              style={{ marginTop: '1.25em', marginBottom: '1.25em' }}
              {...props}
            >
              {children}
            </blockquote>
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
}
